import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authAPI } from '../services/api';

// Reads the `exp` claim (seconds since epoch) out of a JWT without needing a decode library.
function getTokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, dhan_client_id: string, dhan_access_token: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const logout = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Schedules logout to fire the instant the token's `exp` claim is reached, instead of
  // waiting for the next API call to come back with a 401 — matches the 7h backend expiry
  // exactly rather than only reacting to it after the fact.
  const scheduleAutoLogout = (jwt: string) => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    const expiryMs = getTokenExpiryMs(jwt);
    if (!expiryMs) return;
    const delay = expiryMs - Date.now();
    if (delay <= 0) {
      logout();
      return;
    }
    logoutTimerRef.current = setTimeout(logout, delay);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      const expiryMs = getTokenExpiryMs(storedToken);
      if (expiryMs && expiryMs <= Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        scheduleAutoLogout(storedToken);
      }
    }
    setLoading(false);

    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response: AuthResponse = await authAPI.login(username, password);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      setToken(response.token);
      setUser(response.user);
      scheduleAutoLogout(response.token);
    } catch (error) {
      throw error;
    }
  };

  const register = async (username: string, email: string, password: string, dhan_client_id: string, dhan_access_token: string) => {
    try {
      const response: AuthResponse = await authAPI.register(username, email, password, dhan_client_id, dhan_access_token);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      setToken(response.token);
      setUser(response.user);
      scheduleAutoLogout(response.token);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};